import { faCheck, faTimes } from "@fortawesome/pro-light-svg-icons"
import { faCircleCheck, faMicrochipAi } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import React from "react"
import SbEditable from "storyblok-react"
import { render } from "storyblok-rich-text-react-renderer-ts"
import {
  FeatureStoryblok,
  FeatureTitleStoryblok,
} from "../../../component-types-sb"
import { B } from "../typography/Typography"
import Image from "./Image"
import { MarkLinkResolver } from "./Link"
import Tooltip from "./Tooltip"

interface Props {
  readonly blok: FeatureStoryblok | FeatureTitleStoryblok
  readonly circleCheck?: boolean
}

const Feature: React.FC<Props> = ({ blok, circleCheck }) => (
  <SbEditable content={blok}>
    <li tw="flex items-baseline">
      {blok.component === "feature" && (
        <div tw="flex-shrink-0 w-6">
          {blok.icon_image?.filename
            ? (
              <Image image={blok.icon_image} width="16px" />
            )
            : (
              <span>
                {blok.icon === "tick" && (
                  <FontAwesomeIcon
                    tw="text-primary-60 [[data-theme='secondary'] &]:text-blue-police"
                    icon={circleCheck
                      ? faCircleCheck
                      : faCheck}
                  />
                )}
                {blok.icon === "cross" && (
                  <FontAwesomeIcon tw="text-gray-3" icon={faTimes} />
                )}
                {blok.icon === "soon" && (
                  <div tw="left-[-9px] w-[29px] top-[1px] relative">
                    <svg
                      viewBox="0 0 30 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="0.666504"
                        y="0.666656"
                        width="29"
                        height="13"
                        rx="4.37607"
                        fill="#C2A13D"
                      />
                      <path
                        d="M3.20192 8.96966L4.11142 7.87316C4.27009 8.03182 4.47692 8.17632 4.73192 8.30666C4.99259 8.43699 5.27309 8.50216 5.57342 8.50216C5.72642 8.50216 5.87376 8.47099 6.01542 8.40866C6.15709 8.34632 6.22792 8.22732 6.22792 8.05166C6.22792 7.90432 6.15142 7.78249 5.99842 7.68616C5.84542 7.58416 5.62726 7.48782 5.34392 7.39716L5.02942 7.29516C4.79709 7.21582 4.56476 7.11099 4.33242 6.98066C4.10576 6.84466 3.91592 6.67182 3.76292 6.46216C3.61559 6.25249 3.54192 5.99466 3.54192 5.68866C3.54192 5.33732 3.62409 5.02282 3.78842 4.74516C3.95842 4.46749 4.20776 4.24932 4.53642 4.09066C4.86509 3.92632 5.26459 3.84416 5.73492 3.84416C6.10892 3.84416 6.43192 3.88666 6.70392 3.97166C6.98159 4.05666 7.21109 4.15582 7.39242 4.26916C7.57376 4.38249 7.70692 4.48449 7.79192 4.57516L7.02692 5.54416C6.82859 5.39682 6.62176 5.27782 6.40642 5.18716C6.19109 5.09082 5.97009 5.04266 5.74342 5.04266C5.59609 5.04266 5.45442 5.07666 5.31842 5.14466C5.18809 5.20699 5.12292 5.31749 5.12292 5.47616C5.12292 5.65182 5.20226 5.77932 5.36092 5.85866C5.51959 5.93799 5.72076 6.01732 5.96442 6.09666L6.23642 6.18166C6.42909 6.23832 6.62176 6.31199 6.81442 6.40266C7.01276 6.48766 7.19409 6.59816 7.35842 6.73416C7.52276 6.87016 7.65309 7.04299 7.74942 7.25266C7.85142 7.45666 7.90242 7.70882 7.90242 8.00916C7.90242 8.35482 7.79759 8.66366 7.58792 8.93566C7.38392 9.20766 7.10626 9.42016 6.75492 9.57316C6.40926 9.72616 6.02392 9.80266 5.59892 9.80266C5.03226 9.80266 4.55626 9.72616 4.17092 9.57316C3.79126 9.42016 3.46826 9.21899 3.20192 8.96966ZM11.4116 9.80266C10.9866 9.80266 10.5928 9.72899 10.2301 9.58166C9.8731 9.43432 9.56143 9.22749 9.2951 8.96116C9.03443 8.68916 8.83043 8.37182 8.6831 8.00916C8.53576 7.64649 8.4621 7.24982 8.4621 6.81916C8.4621 6.38849 8.53576 5.99182 8.6831 5.62916C8.83043 5.26649 9.03443 4.95199 9.2951 4.68566C9.56143 4.41932 9.8731 4.21249 10.2301 4.06516C10.5928 3.91782 10.9866 3.84416 11.4116 3.84416C11.8366 3.84416 12.2276 3.91782 12.5846 4.06516C12.9473 4.21249 13.2618 4.41932 13.5281 4.68566C13.7944 4.95199 13.9984 5.26649 14.1401 5.62916C14.2874 5.99182 14.3611 6.38849 14.3611 6.81916C14.3611 7.24982 14.2874 7.64649 14.1401 8.00916C13.9984 8.37182 13.7944 8.68916 13.5281 8.96116C13.2618 9.22749 12.9473 9.43432 12.5846 9.58166C12.2276 9.72899 11.8366 9.80266 11.4116 9.80266ZM11.4116 8.39166C11.7119 8.39166 11.9641 8.32082 12.1681 8.17916C12.3778 8.03749 12.5364 7.84766 12.6441 7.60966C12.7518 7.37166 12.8056 7.10816 12.8056 6.81916C12.8056 6.53016 12.7518 6.26666 12.6441 6.02866C12.5364 5.79066 12.3778 5.60082 12.1681 5.45916C11.9641 5.31749 11.7119 5.24666 11.4116 5.24666C11.1169 5.24666 10.8648 5.31749 10.6551 5.45916C10.4511 5.60082 10.2924 5.79066 10.1791 6.02866C10.0714 6.26666 10.0176 6.53016 10.0176 6.81916C10.0176 7.10816 10.0714 7.37166 10.1791 7.60966C10.2924 7.84766 10.4511 8.03749 10.6551 8.17916C10.8648 8.32082 11.1169 8.39166 11.4116 8.39166ZM17.8862 9.80266C17.4612 9.80266 17.0674 9.72899 16.7047 9.58166C16.3477 9.43432 16.036 9.22749 15.7697 8.96116C15.509 8.68916 15.305 8.37182 15.1577 8.00916C15.0104 7.64649 14.9367 7.24982 14.9367 6.81916C14.9367 6.38849 15.0104 5.99182 15.1577 5.62916C15.305 5.26649 15.509 4.95199 15.7697 4.68566C16.036 4.41932 16.3477 4.21249 16.7047 4.06516C17.0674 3.91782 17.4612 3.84416 17.8862 3.84416C18.3112 3.84416 18.7022 3.91782 19.0592 4.06516C19.4219 4.21249 19.7364 4.41932 20.0027 4.68566C20.269 4.95199 20.473 5.26649 20.6147 5.62916C20.762 5.99182 20.8357 6.38849 20.8357 6.81916C20.8357 7.24982 20.762 7.64649 20.6147 8.00916C20.473 8.37182 20.269 8.68916 20.0027 8.96116C19.7364 9.22749 19.4219 9.43432 19.0592 9.58166C18.7022 9.72899 18.3112 9.80266 17.8862 9.80266ZM17.8862 8.39166C18.1865 8.39166 18.4387 8.32082 18.6427 8.17916C18.8524 8.03749 19.011 7.84766 19.1187 7.60966C19.2264 7.37166 19.2802 7.10816 19.2802 6.81916C19.2802 6.53016 19.2264 6.26666 19.1187 6.02866C19.011 5.79066 18.8524 5.60082 18.6427 5.45916C18.4387 5.31749 18.1865 5.24666 17.8862 5.24666C17.5915 5.24666 17.3394 5.31749 17.1297 5.45916C16.9257 5.60082 16.767 5.79066 16.6537 6.02866C16.546 6.26666 16.4922 6.53016 16.4922 6.81916C16.4922 7.10816 16.546 7.37166 16.6537 7.60966C16.767 7.84766 16.9257 8.03749 17.1297 8.17916C17.3394 8.32082 17.5915 8.39166 17.8862 8.39166ZM21.6323 9.66666V3.99716H23.0008L25.1768 7.15916V3.99716H26.6643V9.66666H25.3808L23.1198 6.41116V9.66666H21.6323Z"
                        fill="#F1FBFF"
                      />
                    </svg>
                  </div>
                )}
                {blok.icon === "new" && (
                  <div tw="left-[-6px] w-[24px] top-[1px] relative">
                    {" "}
                    <svg
                      width="25"
                      height="14"
                      viewBox="0 0 25 14"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect
                        x="0.625977"
                        y="0.658081"
                        width="24"
                        height="13"
                        rx="4.36196"
                        fill="#006D41"
                      />
                      <path
                        d="M3.63573 9.65808V3.98858H5.00423L7.18023 7.15058V3.98858H8.66773V9.65808H7.38423L5.12323 6.40258V9.65808H3.63573ZM9.687 9.65808V3.98858H13.767V5.28908H11.1915V6.16458H13.308V7.38858H11.1915V8.35758H13.9455V9.65808H9.687ZM15.6776 9.65808L14.3006 3.98858H15.8901L16.6211 7.78808L17.5136 4.12458H18.8821L19.7831 7.78808L20.5056 3.98858H22.0441L20.6331 9.65808H19.0606L18.1681 6.07108L17.2756 9.65808H15.6776Z"
                        fill="#F1FBFF"
                      />
                    </svg>
                  </div>
                )}
                {blok.icon === "ai" && (
                  <FontAwesomeIcon
                    tw="text-primary-60 h-[19px] left-[-2px] top-[1px] relative"
                    icon={faMicrochipAi}
                  />
                )}
              </span>
            )}
        </div>
      )}
      {blok.text
        && render(blok.text, {
          markResolvers: {
            bold: (children) => <B>{children}</B>,
            link: (children, url) => (
              <MarkLinkResolver url={url} is_blue={blok?.is_blue_link}>
                {children}
              </MarkLinkResolver>
            ),
          },
          nodeResolvers: {
            paragraph: (children) => (
              <span tw="font-light text-left w-fit leading-8">
                {children}
                {blok.tooltip && <Tooltip tooltip={blok.tooltip} />}
              </span>
            ),
          },
        })}
    </li>
  </SbEditable>
)

export default Feature
